/**
 * AdHocBuyerModal — „kontrahent incydentalny": nabywca wpisany wprost na dokumencie, BEZ zapisu
 * do kartoteki. Odpowiednik okna o tej samej nazwie w Symfonii, wywoływanego ikoną przy nazwie
 * kontrahenta.
 *
 * Po co osobne okno, a nie pola wprost w formularzu: to droga WYJĄTKOWA. Domyślnie nabywcę bierze
 * się z kartoteki, bo wtedy dokument wiąże się z historią kontrahenta. Rozłożenie siedmiu pól
 * adresowych na stałe w formularzu sugerowałoby, że tak się pracuje normalnie.
 *
 * NAZWA jest jedynym polem wymaganym — faktura dla osoby fizycznej nie musi mieć NIP-u ani
 * adresu, a wymuszanie ich zablokowałoby poprawne dokumenty.
 *
 * „Pobierz po NIP" korzysta z NASZEGO mechanizmu (Biała lista Ministerstwa Finansów), nie z GUS
 * jak Symfonia — efekt dla użytkownika jest ten sam (nazwa i adres z rejestru), ale źródło jest
 * inne i dlatego przycisk nazywa je wprost.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { HmAdHocBuyer } from '@saas/shared';
import { isAxiosError } from 'axios';
import { Download } from 'lucide-react';

import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface MfLookupResponse {
  found: boolean;
  company: {
    name: string;
    nip: string;
    address: {
      street: string | null;
      buildingNo: string | null;
      apartmentNo: string | null;
      postalCode: string | null;
      city: string | null;
    } | null;
  } | null;
  message?: string;
}

const EMPTY: HmAdHocBuyer = { name: '' };

export function AdHocBuyerModal({
  open,
  initial,
  onClose,
  onSave,
}: {
  open: boolean;
  initial: HmAdHocBuyer | null;
  onClose: () => void;
  onSave: (buyer: HmAdHocBuyer) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [buyer, setBuyer] = useState<HmAdHocBuyer>(initial ?? EMPTY);
  const [looking, setLooking] = useState(false);

  function set(patch: Partial<HmAdHocBuyer>) {
    setBuyer((prev) => ({ ...prev, ...patch }));
  }

  /** Uzupełnia nazwę i adres z rejestru MF. Brak wpisu to normalna odpowiedź, nie awaria. */
  async function lookupByNip() {
    const nip = (buyer.taxId ?? '').replace(/[\s-]/g, '');
    if (!nip) return;
    setLooking(true);
    try {
      const res = await api.get<MfLookupResponse>(`/contractors/lookup-mf?nip=${nip}`);
      const company = res.data.company;
      if (!res.data.found || !company) {
        toast.show({
          variant: 'warning',
          title: t('tenant.handelMagazyn.adHoc.notFound'),
          description: res.data.message,
        });
        return;
      }
      set({
        name: company.name,
        taxId: company.nip,
        street: company.address?.street ?? '',
        houseNo: company.address?.buildingNo ?? '',
        apartmentNo: company.address?.apartmentNo ?? '',
        postCode: company.address?.postalCode ?? '',
        city: company.address?.city ?? '',
      });
    } catch (err) {
      const message = isAxiosError(err)
        ? ((err.response?.data as { message?: string } | undefined)?.message ?? undefined)
        : undefined;
      toast.show({
        variant: 'error',
        title: t('tenant.handelMagazyn.adHoc.lookupFailed'),
        description: message,
      });
    } finally {
      setLooking(false);
    }
  }

  const canSave = buyer.name.trim().length > 0;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.handelMagazyn.adHoc.title')}
      description={t('tenant.handelMagazyn.adHoc.subtitle')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!canSave}
            onClick={() => onSave({ ...buyer, name: buyer.name.trim() })}
          >
            {t('common.save')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex items-end gap-2">
          <Input
            label={t('tenant.handelMagazyn.detail.taxId')}
            value={buyer.taxId ?? ''}
            onChange={(e) => set({ taxId: e.target.value })}
            containerClassName="flex-1"
            data-testid="hm-adhoc-nip"
          />
          <Button
            variant="secondary"
            leftIcon={<Download className="h-4 w-4" />}
            loading={looking}
            disabled={!(buyer.taxId ?? '').trim()}
            onClick={() => void lookupByNip()}
          >
            {t('tenant.handelMagazyn.adHoc.lookup')}
          </Button>
        </div>

        <Input
          label={t('tenant.handelMagazyn.detail.name')}
          value={buyer.name}
          onChange={(e) => set({ name: e.target.value })}
          required
          data-testid="hm-adhoc-name"
        />
        <Input
          label={t('tenant.handelMagazyn.adHoc.street')}
          value={buyer.street ?? ''}
          onChange={(e) => set({ street: e.target.value })}
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            label={t('tenant.handelMagazyn.adHoc.houseNo')}
            value={buyer.houseNo ?? ''}
            onChange={(e) => set({ houseNo: e.target.value })}
          />
          <Input
            label={t('tenant.handelMagazyn.adHoc.apartmentNo')}
            value={buyer.apartmentNo ?? ''}
            onChange={(e) => set({ apartmentNo: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input
            label={t('tenant.handelMagazyn.adHoc.postCode')}
            value={buyer.postCode ?? ''}
            onChange={(e) => set({ postCode: e.target.value })}
          />
          <Input
            label={t('tenant.handelMagazyn.adHoc.city')}
            value={buyer.city ?? ''}
            onChange={(e) => set({ city: e.target.value })}
          />
        </div>
        <Input
          label={t('tenant.handelMagazyn.adHoc.country')}
          value={buyer.country ?? ''}
          onChange={(e) => set({ country: e.target.value })}
          hint={t('tenant.handelMagazyn.adHoc.countryHint')}
        />
      </div>
    </Modal>
  );
}
